import React, { memo } from 'react';
import { renderCircleBase, renderCirclesWithoutLocation } from '@/components/commons/ContactMapping/helpers';

interface Props {
    readonly width?: number;
    readonly contacts: any;
}

function sliceList(list: any, count: number) {
    return list?.slice(0, count) || [];
}

function ContactMappingWithoutFetch({ width = 600, contacts }: Props) {
    if (width < 250 || width > 1000) {
        return null;
    }

    // Define base properties for the circular packing visualization
    const baseProperties = {
        width: width / 2,
        height: width / 2,
        thirdSection: width / 2,
        secondSection: (width / 2) * 0.9,
        firstSection: (width / 2) * 0.6,
        center: (width / 2) * 0.3,
    };

    // Define layers of circles with different properties
    const circleLayers = {
        400: [
            {
                // Distant
                minRadius: baseProperties.firstSection + 10,
                maxRadius: baseProperties.secondSection - 10,
                contacts: sliceList(contacts[2], 10),
                count: 10,
            },
            {
                // Medium
                minRadius: baseProperties.center + 10,
                maxRadius: baseProperties.firstSection - 10,
                contacts: sliceList(contacts[1], 20),
                count: 20,
            },
            {
                // Close
                minRadius: 10,
                maxRadius: baseProperties.center - 10,
                contacts: sliceList(contacts[0], 10),
                count: 10,
            },
        ],
        600: [
            {
                minRadius: baseProperties.firstSection + 10,
                maxRadius: baseProperties.secondSection - 10,
                contacts: sliceList(contacts[2], 30),
                count: 30,
            },
            {
                minRadius: baseProperties.center + 10,
                maxRadius: baseProperties.firstSection - 10,
                contacts: sliceList(contacts[1], 25),
                count: 25,
            },
            {
                minRadius: 10,
                maxRadius: baseProperties.center - 10,
                contacts: sliceList(contacts[0], 15),
                count: 15,
            },
        ],
        1000: [
            {
                minRadius: baseProperties.firstSection + 10,
                maxRadius: baseProperties.secondSection - 10,
                contacts: sliceList(contacts[2], 60),
                count: 60,
            },
            {
                minRadius: baseProperties.center + 10,
                maxRadius: baseProperties.firstSection - 10,
                contacts: sliceList(contacts[1], 40),
                count: 40,
            },
            {
                minRadius: 10,
                maxRadius: baseProperties.center - 10,
                contacts: sliceList(contacts[0], 20),
                count: 20,
            },
        ],
    };

    const defineContactsToShow = () => {
        if (width <= 400 && width >= 250) {
            return circleLayers[400];
        } if (width <= 600 && width >= 400) {
            return circleLayers[600];
        } if (width <= 1000 && width >= 600) {
            return circleLayers[1000];
        }
        return null;
    };

    // Define base properties for the circles
    const circleBase = {
        x: baseProperties.width,
        y: baseProperties.height,
        layers: [
            { radius: baseProperties.thirdSection, color: '#f9fafb' },
            { radius: baseProperties.secondSection, color: '#ecf7f9' },
            { radius: baseProperties.firstSection, color: '#a5bec7' },
            { radius: baseProperties.center, color: '#434f54' },
        ],
    };

    // Return the circular packing visualization
    return (
        <svg width={width} height={width}>
            {renderCircleBase(circleBase)}
            {renderCirclesWithoutLocation(defineContactsToShow(), circleBase, width)}
        </svg>

    );
}

export default memo(ContactMappingWithoutFetch);
